import type { MouseEvent } from 'react'
import { publishMessage } from '@vtex/raccoon-next'

import { buildURLParams, generateFullSiteURL } from './transform'

const ADMIN_RELATIVE_NAVIGATION_TYPE = 'adminRelativeNavigation'
const ADMIN_PREFIX = 'admin'

export const ROUTES = {
  'legacy-catalog': [
    'ProdutoForm.aspx',
    'sku.aspx',
    'SkuForm.aspx',
    'SkuKit.aspx',
    'ProdutoCategoriaSimilar.aspx',
  ],
  pricing: ['#/prices'],
  inventory: [''],
} as const

type AppKeys = keyof typeof ROUTES

export const openAdminInTab =
  <Type extends typeof ROUTES, Key extends keyof Type>(
    app: Key,
    path: Type[Key][keyof Type[Key]],
    account: string,
    workspace?: string,
    params?: Record<string, string | number>
  ) =>
  (evt?: MouseEvent<HTMLDivElement | HTMLButtonElement>) => {
    evt?.stopPropagation()

    if (!ROUTES[app as AppKeys]?.includes(path as never)) {
      throw new Error('Invalid App Route')
    }

    const url = `/${ADMIN_PREFIX}/${app.toString()}${path ? `/${path}` : ''}${
      params ? `?${buildURLParams(params)}` : ''
    }`

    window.open(`${generateFullSiteURL(account, workspace)}${url}`, '_blank')
  }

export const adminNavigate =
  <Type extends typeof ROUTES, Key extends keyof Type>(
    app: Key,
    path: Type[Key][keyof Type[Key]],
    params?: Record<string, string | number>,
    hash?: string
  ) =>
  (evt?: MouseEvent<HTMLDivElement | HTMLButtonElement>) => {
    evt?.stopPropagation()

    if (!ROUTES[app as AppKeys]?.includes(path as never)) {
      throw new Error('Invalid App Route')
    }

    const url = `/${ADMIN_PREFIX}/${app.toString()}${path ? `/${path}` : ''}${
      params ? `?${buildURLParams(params)}` : ''
    }${hash ? `#${hash}` : ''}`

    publishMessage({
      type: ADMIN_RELATIVE_NAVIGATION_TYPE,
      data: {
        path: url,
      },
    })
  }

const isRegularLeftClick = (
  evt: MouseEvent<HTMLDivElement | HTMLAnchorElement>
) => evt.button === 0 && !evt.ctrlKey && !evt.metaKey

export const handleAnchorClickSafely =
  <Callback extends () => void>(callback: Callback) =>
  (evt: MouseEvent<HTMLAnchorElement>) => {
    if (!isRegularLeftClick(evt)) return

    evt.preventDefault()
    callback()
  }
